import * as FullStory from '@fullstory/browser';
import { deleteCookiesByPrefixes } from './utils';
import {
  getSessionStorage,
  setSessionStorage,
} from '../../../../packages/website/src/utils/sessionManager';
import type { analyticSettings } from '../Footer/types/types';

declare const window: any;

function initializeGTM(gtmID: string) {
  if (!gtmID) {
    console.error('GTM ID is not set');
    return;
  }

  // Inject GTM script into the <head>
  try {
    (function (w, d, l, i) {
      w[l] = w[l] || [];
      w[l].push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
      });
      const f = d.getElementsByTagName('script')[0];
      const j = d.createElement('script');
      const dl = l !== 'dataLayer' ? `&l=${l}` : '';
      j.async = true;
      j.src = `https://www.googletagmanager.com/gtm.js?id=${i}${dl}`;
      f.parentNode.insertBefore(j, f);
    })(window, document, 'dataLayer', gtmID);

    // Inject GTM noscript iframe into the <body>
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmID}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);

    // Append to <body>
    if (document.body) {
      document.body.appendChild(noscript);
    } else {
      console.error('document.body is not available');
    }
  } catch (e) {
    console.error(e);
  }
}

function removeGTM() {
  // Remove GTM script
  const gtmScript = document.querySelector(
    'script[src*="googletagmanager.com/gtm.js"]'
  );
  if (gtmScript) {
    gtmScript.parentNode.removeChild(gtmScript);
  }

  // Remove GTM iframe
  const gtmIframe = document.querySelector(
    'iframe[src*="googletagmanager.com/ns.html"]'
  );
  if (gtmIframe) {
    gtmIframe.parentNode.removeChild(gtmIframe);
  }

  deleteCookiesByPrefixes(['_ga', '_gcl']);
}

export const optGTM = (enable: boolean) => {
  if (!enable) {
    removeGTM();
    return;
  }
  initializeGTM('GTM-MX2HJWM');
};

function initApollo() {
  const appID = process.env.GATSBY_APOLLO_APP_ID;
  const n = Math.random().toString(36).substring(7);
  const o = document.createElement('script');

  o.src = `https://assets.apollo.io/micro/website-tracker/tracker.iife.js?nocache=${n}`;
  o.async = true;
  o.defer = true;

  o.onload = () => {
    if (window.trackingFunctions?.onLoad) {
      window.trackingFunctions.onLoad({
        appId: appID,
      });
    } else {
      console.error('Apollo tracking functions are not available.');
    }
  };
  document.head.appendChild(o);
}

function removeApollo() {
  const apolloScript = document.querySelector(
    'script[src*="assets.apollo.io/micro/website-tracker/tracker.iife.js"]'
  );
  if (apolloScript) {
    apolloScript.parentNode.removeChild(apolloScript);
    delete window.trackingFunctions;
  }
  deleteCookiesByPrefixes(['apollo']);
}

export function optApollo(enable: boolean) {
  if (!enable) {
    removeApollo();
    return;
  }
  initApollo();
}

const showToPercentage = (max: number) => {
  return Math.floor(Math.random() * Math.floor(max));
};

const initFullStory = () => {
  const fullStory = getSessionStorage('fullStory');
  // verifies we are in prod - random initialization of fullstory if new session
  if (process.env.GATSBY_TARGET_ENV === '1' && fullStory !== 'true') {
    if (showToPercentage(10) === 0) {
      FullStory.init({
        orgId: process.env.GATSBY_FULLSTORY_ORG_ID,
        recordOnlyThisIFrame: true,
      });
      setSessionStorage('fullStory', 'true');
    }
  }
};

const removeFullStory = () => {
  if (window.FS) {
    window.FS.shutdown();
    delete window.FS;
  }
  setSessionStorage('fullStory', 'false');
  deleteCookiesByPrefixes(['fs_', '_fs']);
};

export const optFullStory = (enable: boolean) => {
  if (!enable) {
    removeFullStory();
    return;
  }
  initFullStory();
  return;
};

function injectVWO(): void {
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.id = 'vwoCode';
  script.innerHTML = `
window._vwo_code || (function() {
var account_id=969206,
version=2.1,
settings_tolerance=2000,
hide_element='body',
hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important;transition:none !important;',
f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={nonce:v&&v.nonce,use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){if(performance.getEntriesByName('first-contentful-paint')[0]){return''}return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(e){if(!f){f=true;var t=d.getElementById('_vis_opt_path_hides');if(t)t.parentNode.removeChild(t);if(e)(new Image).src='https://dev.visualwebsiteoptimizer.com/ee.gif?a='+account_id+e}},finished:function(){return f},addScript:function(e){var t=d.createElement('script');t.type='text/javascript';if(e.src){t.src=e.src}else{t.text=e.text}v&&t.setAttribute('nonce',v.nonce);d.getElementsByTagName('head')[0].appendChild(t)},load:function(e,t){var n=this.getSettings(),i=d.createElement('script'),r=this;t=t||{};if(n){i.textContent=n;d.getElementsByTagName('head')[0].appendChild(i);if(!w.VWO||VWO.caE){stT.removeItem(cK);r.load(e)}}else{var o=new XMLHttpRequest;o.open('GET',e,true);o.withCredentials=!t.dSC;o.responseType=t.responseType||'text';o.onload=function(){if(t.onloadCb){return t.onloadCb(o,e)}if(o.status===200||o.status===304){_vwo_code.addScript({text:o.responseText})}else{_vwo_code.finish('&e=loading_failure:'+e)}};o.onerror=function(){if(t.onerrorCb){return t.onerrorCb(e)}_vwo_code.finish('&e=loading_failure:'+e)};o.send()}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){_vwo_code.finish();stT.removeItem(cK)},e);var t;if(this.hide_element()!=='body'){t=d.createElement('style');var n=this.hide_element(),i=n?n+this.hide_element_style():'',r=d.getElementsByTagName('head')[0];t.setAttribute('id','_vis_opt_path_hides');v&&t.setAttribute('nonce',v.nonce);t.setAttribute('type','text/css');if(t.styleSheet)t.styleSheet.cssText=i;else t.appendChild(d.createTextNode(i));r.appendChild(t)}else{t=d.getElementsByTagName('head')[0];var i=d.createElement('div');i.style.cssText='z-index: 2147483647 !important;position: fixed !important;left: 0 !important;top: 0 !important;width: 100% !important;height: 100% !important;background: white !important;display: block !important;';i.setAttribute('id','_vis_opt_path_hides');i.classList.add('_vis_hide_layer');t.parentNode.insertBefore(i,t.nextSibling)}var o=window._vis_opt_url||d.URL,s='https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(o)+'&vn='+version;if(w.location.search.indexOf('_vwo_xhr')!==-1){this.addScript({src:s})}else{this.load(s+'&x=true')}}};w._vwo_code=code;code.init();})();`;
  script.setAttribute('data-testid', 'vwo-script');
  document.head.appendChild(script);
}

const removeVWO = () => {
  window.VWO = window.VWO || [];
  window.VWO.push(['optOutVisitor', { maintainExperiences: true }]);
  deleteCookiesByPrefixes(['_vwo', '_vis']);
};

export const optVWO = (enable: boolean) => {
  if (!enable) {
    removeVWO();
    return;
  }
  injectVWO();
};

const trackingHandlers = [
  { key: 'fullstory', handler: optFullStory },
  { key: 'apollo', handler: optApollo },
  { key: 'vwo', handler: optVWO },
  { key: 'gtm', handler: optGTM },
];

export const handleTrackingOptions = (
  trackingOptions: analyticSettings,
  enable: boolean
) => {
  trackingHandlers.forEach(({ key, handler }) => {
    if (trackingOptions?.[key]) {
      handler(enable);
    }
  });
};
